import { marked } from 'marked';
import { htmlToMarkdown } from '../../utils/html-to-markdown.js';
import { chipsToAnchors } from '../references/utils.js';


/**
 * Builds a context key for keying newChatDrafts and newChatIds by context.
 * - BOARD:      'board_${boardId}'
 * - CARD:       'card_${boardId}_${cardId}'
 * - HOME:       'home'
 * - ONBOARDING: 'onboarding'
 *
 * @param {String} contextType - 'BOARD' | 'CARD' | 'HOME' | 'ONBOARDING'
 * @param {String} [boardId]
 * @param {String} [cardId]
 * @returns {String|null} context key, or null if contextType is missing
 */
export const buildContextKey = (contextType, boardId, cardId) => {
  if (!contextType) return null;
  const type = contextType.toLowerCase();
  if (type === 'home' || type === 'onboarding') return type;
  if (type === 'card') return `card_${boardId}_${cardId}`;
  return `${type}_${boardId}`;
};

/**
 * Checks if URL is an AI Chat link
 * @param {String} url - URL to check
 * @returns {Boolean} true if URL contains #ai-chat/{chatId} pattern
 */
export const isAiChatLink = (url) => {
  if (!url) return false;
  const regex = /\#ai-chat\/([a-zA-Z0-9_-]+)(?:\/([a-zA-Z0-9_-]+))?/;
  return regex.test(url);
}

/**
 * Drops the upload anchors `<a data-type="attachment" data-id>` whose id is in `idSet` from a draft.
 *
 * @param {String} html Draft HTML
 * @param {Set<String>} idSet Attachment IDs to strip
 * @returns {String} cleaned HTML, or the original string if nothing changed
 */
export const stripChipsByIds = (html, idSet) => {
  if (!html || typeof html !== 'string' || !idSet?.size || !html.includes('data-type="attachment"')) return html;

  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const anchors = [...body.querySelectorAll('a[data-type="attachment"][data-id]')].filter((anchor) => idSet.has(anchor.getAttribute('data-id')));
  anchors.forEach((anchor) => anchor.remove());
  return anchors.length ? body.innerHTML : html;
};

/**
 * The editor's HTML as the Markdown `userPrompt`: reference anchors, underline and highlight stay inline HTML.
 * @returns {Promise<String>}
 */
export const promptToMarkdown = (html) => htmlToMarkdown(html, { keepReferenceAnchors: true });

/**
 * A sent `userPrompt` as the editor's HTML. Markdown passes inline HTML through, so an old HTML prompt shows as
 * it was; an older one holding the Squire chip elements gets them as anchors first, `fallback` as `chipsToAnchors`'s.
 */
export const promptToEditorValue = (userPrompt, fallback) => marked(chipsToAnchors(userPrompt || '', fallback), { breaks: true });

/**
 * Whether a quick action's `filter` lets it run in `scope`, `{ trigger: 'BUTTON' | 'LIST', draft }`.
 * `LIST` is the AI Helper itself: its quick-action list and the conversation the action starts.
 *
 * The filter is clauses joined by `&&`, each `<path> == null`, `<path> != null` or `<path> == 'VALUE'`,
 * e.g. `trigger == 'BUTTON' && draft.chatMessage.text != null`. Text with no visible characters counts
 * as null. No filter lets it run anywhere; a clause that does not parse stops it running. Never `eval`, per CSP.
 */
export const isQuickActionAllowed = (quickAction, scope) => {
  const filter = quickAction?.filter?.trim();
  if (!filter) return true;

  return filter.split('&&').every((clause) => {
    const match = clause.trim().match(/^([\w.]+)\s*(==|!=)\s*(null|'[^']*')$/);
    if (!match) return false;

    const [, path, operator, expected] = match;
    let value = path.split('.').reduce((object, key) => object?.[key], scope);
    if (typeof value === 'string' && !value.replace(/<[^>]*>|&nbsp;/g, '').trim()) value = null;
    const isEqual = expected === 'null' ? value == null : value === expected.slice(1, -1);
    return operator === '==' ? isEqual : !isEqual;
  });
};
